import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/common";

export const MODULE_LABEL = { task: "Ops Task", office_task: "Office Task", order: "Order", sample: "Sample", exception: "Exception", stock_adjustment: "Stock Adjustment",
  price_approval: "Price Approval", production: "Production", shipment: "Dispatch", reimbursement: "Reimbursement" };
export const errMsg = (e, fb) => { const d = e.response?.data?.detail; return typeof d === "string" ? d : d?.message || fb; };

export function OwnerChip({ row }) {
  if (row.assigned_name) return <span className="text-xs" data-testid={`owner-${row.module}-${row.item_id}`}><span className="font-medium">{row.assigned_name}</span>{row.designation ? <span className="text-muted-foreground"> – {row.designation}</span> : ""}</span>;
  return <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-signal-red/15 text-signal-red" data-testid={`owner-${row.module}-${row.item_id}`}>{row.ownership === "reassignment_required" ? "Reassignment required" : "Assignee required"}</span>;
}

export function DeptUserPicker({ department, value, onChange, testid = "dept-user-picker" }) {
  const [users, setUsers] = useState([]);
  useEffect(() => { if (department) api.get(`/work/department-users?department=${encodeURIComponent(department)}`).then((r) => setUsers(r.data.users)).catch(() => setUsers([])); else setUsers([]); }, [department]);
  return (
    <select className="h-9 rounded-md border border-input bg-background px-2 text-xs w-full" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} disabled={!department}>
      <option value="">{department ? `Select ${department} user…` : "Select department first"}</option>
      {users.map((u) => <option key={u.user_id} value={u.user_id}>{`${u.name} – ${u.designation || u.role}`}</option>)}
    </select>
  );
}

export function AssignDialog({ row, departments, onDone, onClose }) {
  const [dept, setDept] = useState(row.department || "");
  const [uid, setUid] = useState("");
  const [reason, setReason] = useState("");
  const [due, setDue] = useState("");
  const submit = async () => {
    try {
      await api.post("/work/assign", { module: row.module, item_id: row.item_id, user_id: uid || null, department: dept || null, reason: reason || null, due_at: due || null });
      toast.success(uid ? "Responsibility assigned" : "Marked assignee required — department manager notified"); onDone();
    } catch (e) { toast.error(errMsg(e, "Assign failed")); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="assign-dialog">
      <div className="bg-card border border-border rounded-lg p-4 w-full max-w-md space-y-3">
        <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Assign responsibility · {MODULE_LABEL[row.module]} {row.item_id}</p>
        <p className="text-sm">{row.title}</p>
        <select className="h-9 rounded-md border border-input bg-background px-2 text-xs w-full" value={dept} onChange={(e) => { setDept(e.target.value); setUid(""); }} data-testid="assign-department">
          <option value="">Department…</option>{departments.map((d) => <option key={d.name} value={d.name}>{d.name}</option>)}
        </select>
        <DeptUserPicker department={dept} value={uid} onChange={setUid} testid="assign-user" />
        <Input type="datetime-local" value={due} onChange={(e) => setDue(e.target.value)} data-testid="assign-due" />
        <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for (re)assignment" data-testid="assign-reason" />
        <div className="flex justify-end gap-2"><Button size="sm" variant="ghost" onClick={onClose}>Cancel</Button><Button size="sm" className="bg-primary text-primary-foreground" onClick={submit} data-testid="assign-submit">Assign</Button></div>
      </div>
    </div>
  );
}

export function WorkTable({ rows, showOwner = true, onAssign, testid = "work-table" }) {
  const navigate = useNavigate();
  return (
    <div className="bg-card border border-border rounded-lg overflow-x-auto">
      <table className="w-full text-sm" data-testid={testid}>
        <thead><tr className="border-b border-border text-left">{["Module", "Item", "Action / Title", "Department", showOwner && "Responsible user", "Due", "Status", onAssign && " "].filter(Boolean).map((h) => <th key={h} className="px-3 py-2.5 text-[10px] font-mono uppercase tracking-widest text-muted-foreground whitespace-nowrap">{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.module}-${r.item_id}`} className={`border-b border-border/50 hover:bg-accent/40 ${r.overdue ? "bg-signal-red/5" : ""}`} data-testid={`work-row-${r.module}-${r.item_id}`}>
              <td className="px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground whitespace-nowrap">{MODULE_LABEL[r.module] || r.module}</td>
              <td className="px-3 py-2 font-mono text-xs text-primary font-bold cursor-pointer" onClick={() => navigate(r.link)}>{r.item_id}</td>
              <td className="px-3 py-2 text-xs">{r.title}</td>
              <td className="px-3 py-2 text-xs text-muted-foreground">{r.department || "—"}</td>
              {showOwner && <td className="px-3 py-2"><OwnerChip row={r} /></td>}
              <td className={`px-3 py-2 text-xs whitespace-nowrap ${r.overdue ? "text-signal-red font-bold" : ""}`}>{r.due ? String(r.due).slice(0, 16).replace("T", " ") : "—"}{r.overdue && " · OVERDUE"}</td>
              <td className="px-3 py-2"><StatusBadge status={r.status || "open"} /></td>
              {onAssign && <td className="px-3 py-2"><button className="text-xs underline text-primary" onClick={() => onAssign(r)} data-testid={`assign-${r.module}-${r.item_id}`}>{r.assigned_name ? "Reassign" : "Assign"}</button></td>}
            </tr>))}
          {rows.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-muted-foreground">Nothing here.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
